import React, { useState, useRef, useEffect } from 'react';
import { contractTypes, counterparties, assets } from '../../data/dummyData';
import { Upload, File, X, FileText, Image, FileDown, BookOpen, ShieldAlert, AlertTriangle } from 'lucide-react';
import ClauseManager from './ClauseManager';
import ClauseSelector from './ClauseSelector';
import ApprovalWorkflow from './ApprovalWorkflow';
import MasterDataService from '../../services/masterDataService';

// Contract Form Component with 3x2 Grid Layout
const ContractForm = ({ contract, onSave, onCancel }) => {

  // Form data state - Initialize first
  const [formData, setFormData] = useState({
    // Basic Information
    contractId: contract?.id || '',
    title: contract?.title || '',
    type: contract?.type || '',
    contractLevel: contract?.contractLevel || 'Main',
    parentContractId: contract?.parentContractId || '',
    riskLevel: contract?.riskLevel || 'Medium',

    // Parties & Dates
    counterparty: contract?.counterparty || '',
    startDate: contract?.startDate || '',
    endDate: contract?.endDate || '',
    status: contract?.status || 'Draft',
    priority: contract?.priority || 'Medium',

    // Financial Information
    amount: contract?.amount || '',
    currency: contract?.currency || 'USD',
    paymentTerms: contract?.paymentTerms || '',
    frequency: contract?.frequency || '',

    // Business Details
    department: contract?.department || '',
    location: contract?.location || '',
    autoRenewal: contract?.autoRenewal || false,
    approvalLevel: contract?.approvalLevel || 'Manager',
    requiresLegalReview: contract?.requiresLegalReview || false,
    requiresFinanceReview: contract?.requiresFinanceReview || false,

    // Legal & Compliance
    governingLaw: contract?.governingLaw || '',
    jurisdiction: contract?.jurisdiction || '',
    noticePeriod: contract?.noticePeriod || '',

    // Additional Information
    description: contract?.description || '',
    clauses: contract?.clauses || []
  });

  const [complianceErrors, setComplianceErrors] = useState([]);
  const [showClauseSelector, setShowClauseSelector] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessages, setWarningMessages] = useState([]);
  const [pendingSubmission, setPendingSubmission] = useState(null);

  // Master data state
  const [masterData, setMasterData] = useState({
    contractTypes: [],
    statuses: [],
    priorities: [],
    currencies: [],
    paymentTerms: [],
    frequencies: [],
    departments: [],
    locations: []
  });

  // Load master data on component mount
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const response = await MasterDataService.getContractDropdowns();
        const dropdownData = response.data;

        setMasterData({
          contractTypes: dropdownData.ContractType?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          statuses: dropdownData.Status?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          priorities: dropdownData.Priority?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          currencies: dropdownData.Currency?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          paymentTerms: dropdownData.PaymentTerms?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          frequencies: dropdownData.Frequency?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          departments: dropdownData.Department?.map(item => ({
            value: item.code,
            label: item.label
          })) || [],
          locations: dropdownData.Location?.map(item => ({
            value: item.code,
            label: item.label
          })) || []
        });
      } catch (error) {
        console.error('Error loading master data:', error);

        // Set fallback master data when API fails
        setMasterData({
          contractTypes: [
            { value: 'Service Agreement', label: 'Service Agreement' },
            { value: 'Purchase Order', label: 'Purchase Order' },
            { value: 'Lease Agreement', label: 'Lease Agreement' },
            { value: 'Employment Contract', label: 'Employment Contract' },
            { value: 'NDA', label: 'NDA' },
            { value: 'SLA', label: 'SLA' },
            { value: 'Partnership', label: 'Partnership' },
            { value: 'Licensing', label: 'Licensing' },
            { value: 'Maintenance', label: 'Maintenance' },
            { value: 'Consulting', label: 'Consulting' },
            { value: 'Sub-Contract', label: 'Sub-Contract' },
            { value: 'Framework', label: 'Framework' }
          ],
          statuses: [
            { value: 'Draft', label: 'Draft' },
            { value: 'Active', label: 'Active' },
            { value: 'Expired', label: 'Expired' },
            { value: 'Terminated', label: 'Terminated' }
          ],
          priorities: [
            { value: 'Low', label: 'Low' },
            { value: 'Medium', label: 'Medium' },
            { value: 'High', label: 'High' },
            { value: 'Critical', label: 'Critical' }
          ],
          currencies: [
            { value: 'USD', label: 'US Dollar' },
            { value: 'EUR', label: 'Euro' },
            { value: 'GBP', label: 'British Pound' },
            { value: 'CAD', label: 'Canadian Dollar' },
            { value: 'AUD', label: 'Australian Dollar' }
          ],
          paymentTerms: [
            { value: 'Net 30', label: 'Net 30' },
            { value: 'Net 60', label: 'Net 60' },
            { value: 'Net 90', label: 'Net 90' },
            { value: 'Due on Receipt', label: 'Due on Receipt' },
            { value: '50% Upfront', label: '50% Upfront' }
          ],
          frequencies: [
            { value: 'Monthly', label: 'Monthly' },
            { value: 'Quarterly', label: 'Quarterly' },
            { value: 'Annually', label: 'Annually' },
            { value: 'One-time', label: 'One-time' },
            { value: 'Milestone-based', label: 'Milestone-based' }
          ],
          departments: [
            { value: 'IT', label: 'IT' },
            { value: 'HR', label: 'HR' },
            { value: 'Finance', label: 'Finance' },
            { value: 'Legal', label: 'Legal' },
            { value: 'Operations', label: 'Operations' },
            { value: 'Sales', label: 'Sales' },
            { value: 'Marketing', label: 'Marketing' }
          ],
          locations: [
            { value: 'New York', label: 'New York' },
            { value: 'California', label: 'California' },
            { value: 'Texas', label: 'Texas' },
            { value: 'London', label: 'London' },
            { value: 'Toronto', label: 'Toronto' },
            { value: 'Sydney', label: 'Sydney' }
          ]
        });
      }
    };

    loadMasterData();
  }, []);


  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState(contract?.documents || []);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAssetToggle = (assetId) => {
    setFormData(prev => ({
      ...prev,
      linkedAssets: prev.linkedAssets.includes(assetId)
        ? prev.linkedAssets.filter(id => id !== assetId)
        : [...prev.linkedAssets, assetId]
    }));
  };

  // File upload handlers
  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      uploadedAt: new Date().toISOString()
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };


  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image size={16} />;
    if (fileType.includes('pdf')) return <FileText size={16} />;
    return <File size={16} />;
  };

  const handleClausesChange = (clauses) => {
    setFormData(prev => ({
      ...prev,
      clauses: clauses
    }));
  };

  const handleApprovalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setComplianceErrors([]);

    // Validate required fields before submission
    if (!formData.title || !formData.title.trim()) {
      alert('Please enter a contract title');
      return;
    }
    if (!formData.type || !formData.type.trim()) {
      alert('Please select a contract type');
      return;
    }
    if (!formData.counterparty || !formData.counterparty.trim()) {
      alert('Please select a counterparty');
      return;
    }
    if (!formData.startDate) {
      alert('Please select a start date');
      return;
    }
    if (!formData.endDate) {
      alert('Please select an end date');
      return;
    }

    // Transform form data to match backend model requirements
    const transformedData = {
      // Basic required fields - ensure type is preserved and trimmed
      title: formData.title.trim(),
      type: formData.type.trim(), // Ensure type is trimmed and preserved
      createdFrom: 'Manual',
      counterparty: formData.counterparty.trim(),
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      status: formData.status || 'Draft',
      amount: formData.amount ? parseFloat(formData.amount) : 0,
      currency: formData.currency || 'USD',
      paymentTerms: formData.paymentTerms || 'NET_30',
      frequency: formData.frequency || 'MONTHLY',

      // Optional fields
      contractId: formData.contractId ? formData.contractId.trim() : undefined, // Will be auto-generated if not provided
      priority: formData.priority || 'MEDIUM',
      autoRenewal: formData.autoRenewal || false,
      description: formData.description ? formData.description.trim() : '',
      terms: formData.terms ? formData.terms.trim() : '',
      conditions: formData.conditions ? formData.conditions.trim() : '',
      noticePeriod: formData.noticePeriod ? parseInt(formData.noticePeriod) || 30 : 30,
      renewalTerms: formData.renewalTerms ? formData.renewalTerms.trim() : '',
      terminationClause: formData.terminationClause ? formData.terminationClause.trim() : '',

      // Documents
      documents: uploadedFiles.map(file => ({
        name: file.name,
        type: file.type,
        uploadedAt: file.uploadedAt || new Date().toISOString()
      })),

      // Tags and custom fields
      tags: formData.tags || [],
      customFields: formData.customFields || {}
    };

    // Remove undefined or empty contractId to allow auto-generation
    if (!transformedData.contractId) {
      delete transformedData.contractId;
    }

    try {
      // Try to save normally
      await onSave(transformedData);
    } catch (error) {

      if (error.response) {
        // CASE 1: Critical Block (400)
        if (error.response.status === 400 && error.response.data.errors) {
          setComplianceErrors(error.response.data.errors);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // CASE 2: Warning Confirmation (409) - NEW!
        else if (error.response.status === 409 && error.response.data.requireConfirmation) {
          setWarningMessages(error.response.data.warnings);
          setPendingSubmission(transformedData); // Save the data
          setShowWarningModal(true);     // Open the popup
        }
        else {
          alert(error.response.data.message || 'Failed to create contract');
        }
      }
    }
  };

  const handleConfirmWarnings = async () => {
    if (!pendingSubmission) return;

    const confirmedData = {
      ...pendingSubmission,
      confirmWarnings: true // Add the flag
    };

    try {
      await onSave(confirmedData);
      setShowWarningModal(false); // Close modal on success
      setPendingSubmission(null);
    } catch (error) {
      console.error("Retry error:", error);
      // Handle error again if needed
    }
  };

  // Enhanced test data function - Populates ALL form fields
  const [testDataLoaded, setTestDataLoaded] = useState(false);

  const loadTestData = () => {
    // First, ensure master data is loaded with fallback values
    setMasterData({
      contractTypes: [
        { value: 'NDA', label: 'Non-Disclosure Agreement' },
        { value: 'DPA', label: 'Data Processing Agreement' },
        { value: 'MSA', label: 'Master Service Agreement' },
        { value: 'SOW', label: 'Statement of Work' },
        { value: 'PO', label: 'Purchase Order' },
        { value: 'SUBSCRIPTION', label: 'Subscription Agreement' },
        { value: 'SERVICE', label: 'Service Agreement' },
        { value: 'SLA', label: 'Service Level Agreement' },
        { value: 'AMENDMENT', label: 'Amendment' },
        { value: 'RENEWAL', label: 'Renewal' },
        { value: 'CHANGE_ORDER', label: 'Change Order' },
        { value: 'MOU', label: 'Memorandum of Understanding' },
        { value: 'LICENSE', label: 'License Agreement' }
      ],
      statuses: [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'IN_REVIEW', label: 'In Review' },
        { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'EXECUTED', label: 'Executed' },
        { value: 'ON_HOLD', label: 'On Hold' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'EXPIRED', label: 'Expired' },
        { value: 'CANCELLED', label: 'Cancelled' }
      ],
      priorities: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' },
        { value: 'Critical', label: 'Critical' }
      ],
      currencies: [
        { value: 'USD', label: 'US Dollar' },
        { value: 'EUR', label: 'Euro' },
        { value: 'GBP', label: 'British Pound' },
        { value: 'CAD', label: 'Canadian Dollar' },
        { value: 'AUD', label: 'Australian Dollar' }
      ],
      paymentTerms: [
        { value: 'Net 30', label: 'Net 30' },
        { value: 'Net 60', label: 'Net 60' },
        { value: 'Net 90', label: 'Net 90' },
        { value: 'Due on Receipt', label: 'Due on Receipt' },
        { value: '50% Upfront', label: '50% Upfront' }
      ],
      frequencies: [
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Annually', label: 'Annually' },
        { value: 'One-time', label: 'One-time' },
        { value: 'Milestone-based', label: 'Milestone-based' }
      ],
      departments: [
        { value: 'IT', label: 'IT' },
        { value: 'HR', label: 'HR' },
        { value: 'Finance', label: 'Finance' },
        { value: 'Legal', label: 'Legal' },
        { value: 'Operations', label: 'Operations' },
        { value: 'Sales', label: 'Sales' },
        { value: 'Marketing', label: 'Marketing' }
      ],
      locations: [
        { value: 'New York', label: 'New York' },
        { value: 'California', label: 'California' },
        { value: 'Texas', label: 'Texas' },
        { value: 'London', label: 'London' },
        { value: 'Toronto', label: 'Toronto' },
        { value: 'Sydney', label: 'Sydney' }
      ]
    });

    // Then set the form data with test values (matching backend model)
    const testData = {
      // Basic Information
      contractId: 'CT-2024-001',
      title: 'Software Development and Maintenance Agreement',
      type: 'SERVICE', // Using backend enum value
      contractLevel: 'Main',
      parentContractId: '',
      riskLevel: 'Medium',

      // Parties & Dates
      counterparty: 'Tech Solutions Inc.',
      startDate: '2024-01-15',
      endDate: '2025-01-14',
      status: 'DRAFT', // Using backend enum value
      priority: 'High',

      // Financial Information
      amount: '75000',
      currency: 'USD',
      paymentTerms: 'Net 30',
      frequency: 'Monthly',

      // Business Details
      department: 'IT',
      location: 'New York',
      autoRenewal: true,
      approvalLevel: 'Manager',
      requiresLegalReview: true,
      requiresFinanceReview: false,

      // Legal & Compliance
      governingLaw: 'US Federal',
      jurisdiction: 'New York, NY',
      noticePeriod: 30, // Number, not string

      // Additional Information
      description: 'Comprehensive software development and maintenance agreement for web application development, including ongoing support and updates. This contract covers the full lifecycle of the project from initial development through post-launch maintenance and support.'
    };

    setFormData(testData);
    setTestDataLoaded(true);
    console.log('✅ Enhanced test data loaded with ALL fields and master data:', testData);

    // Reset the success indicator after 3 seconds
    setTimeout(() => {
      setTestDataLoaded(false);
    }, 3000);
  };

  return (
    <form onSubmit={handleSubmit} style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      width: '100%',
      maxWidth: '100%'
    }}>
      {/* COMPLIANCE ERROR BANNER */}
      {complianceErrors.length > 0 && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #EF4444',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#B91C1C', marginBottom: '8px' }}>
            <ShieldAlert size={20} />
            <span style={{ fontWeight: '600', fontSize: '15px' }}>Contract Blocked by Compliance Policy</span>
          </div>
          <div style={{ paddingLeft: '28px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#7F1D1D' }}>
              The following policies are preventing this contract from being created:
            </p>
            <ul style={{ margin: 0, paddingLeft: '16px', color: '#991B1B' }}>
              {complianceErrors.map((err, idx) => (
                <li key={idx} style={{ marginBottom: '4px', fontSize: '14px' }}>
                  <strong>{err.policyName}:</strong> {err.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* Test Data Button - Only visible in development mode */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ marginBottom: '20px', textAlign: 'right' }}>
          <button
            type="button"
            onClick={loadTestData}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: testDataLoaded ? '#059669' : '#10b981',
              border: `1px solid ${testDataLoaded ? '#059669' : '#10b981'}`,
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              transition: 'all 0.2s ease',
              transform: testDataLoaded ? 'scale(1.05)' : 'scale(1)'
            }}
            onMouseOver={(e) => {
              if (!testDataLoaded) {
                e.target.style.backgroundColor = '#059669';
                e.target.style.borderColor = '#059669';
              }
            }}
            onMouseOut={(e) => {
              if (!testDataLoaded) {
                e.target.style.backgroundColor = '#10b981';
                e.target.style.borderColor = '#10b981';
              }
            }}
            title="Load test data for development - Populates ALL form fields including dropdowns"
          >
            {testDataLoaded ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Data Loaded!
              </>
            ) : (
              <>
                <FileText size={14} />
                Load Test Data
              </>
            )}
          </button>
        </div>
      )}

      {/* Row 1: Contract ID, Title, Type */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Contract ID
            </label>
            <input
              type="text"
              name="contractId"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.contractId}
              onChange={handleChange}
              placeholder="e.g., CT-2024-001"
            />
          </div>
        </div>
        <div style={{ flex: '2', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Contract Title *
            </label>
            <input
              type="text"
              name="title"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter contract title"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Contract Type *
            </label>
            <select
              name="type"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="">Select Type</option>
              <option value="Service Agreement">Service Agreement</option>
              <option value="Purchase Order">Purchase Order</option>
              <option value="Lease Agreement">Lease Agreement</option>
              <option value="Employment Contract">Employment Contract</option>
              <option value="NDA">Non-Disclosure Agreement</option>
              <option value="SLA">Service Level Agreement</option>
              <option value="Partnership">Partnership Agreement</option>
              <option value="Licensing">Licensing Agreement</option>
              <option value="Maintenance">Maintenance Contract</option>
              <option value="Consulting">Consulting Agreement</option>
              <option value="Sub-Contract">Sub-Contract</option>
              <option value="Framework">Framework Agreement</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 1.5: Contract Hierarchy */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Contract Level
            </label>
            <select
              name="contractLevel"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.contractLevel}
              onChange={handleChange}
            >
              <option value="Main">Main Contract</option>
              <option value="Sub-Contract">Sub-Contract</option>
              <option value="Amendment">Amendment</option>
              <option value="Addendum">Addendum</option>
              <option value="Renewal">Renewal</option>
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Parent Contract
            </label>
            <input
              type="text"
              name="parentContractId"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.parentContractId}
              onChange={handleChange}
              placeholder="Parent contract ID (if applicable)"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Risk Level
            </label>
            <select
              name="riskLevel"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.riskLevel}
              onChange={handleChange}
            >
              <option value="Low">Low Risk</option>
              <option value="Medium">Medium Risk</option>
              <option value="High">High Risk</option>
              <option value="Critical">Critical Risk</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 2: Counterparty, Start Date, End Date */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Counterparty *
            </label>
            <select
              name="counterparty"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.counterparty}
              onChange={handleChange}
              required
            >
              <option value="">Select Counterparty</option>
              {counterparties.map(cp => (
                <option key={cp.id} value={cp.name}>{cp.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Start Date *
            </label>
            <input
              type="date"
              name="startDate"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.startDate}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              End Date *
            </label>
            <input
              type="date"
              name="endDate"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.endDate}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      {/* Row 3: Status, Priority, Amount */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Status
            </label>
            <select
              name="status"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.status}
              onChange={handleChange}
            >
              <option value="">Select Status</option>
              {masterData.statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Priority
            </label>
            <select
              name="priority"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.priority}
              onChange={handleChange}
            >
              <option value="">Select Priority</option>
              {masterData.priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Amount
            </label>
            <input
              type="number"
              name="amount"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
            />
          </div>
        </div>
      </div>

      {/* Row 4: Payment Terms, Frequency, Currency */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Payment Terms
            </label>
            <select
              name="paymentTerms"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.paymentTerms}
              onChange={handleChange}
            >
              <option value="">Select Payment Terms</option>
              {masterData.paymentTerms.map(term => (
                <option key={term.value} value={term.value}>
                  {term.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Frequency
            </label>
            <select
              name="frequency"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.frequency}
              onChange={handleChange}
            >
              <option value="">Select Frequency</option>
              {masterData.frequencies.map(frequency => (
                <option key={frequency.value} value={frequency.value}>
                  {frequency.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Currency
            </label>
            <select
              name="currency"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.currency}
              onChange={handleChange}
            >
              <option value="">Select Currency</option>
              {masterData.currencies.map(currency => (
                <option key={currency.value} value={currency.value}>
                  {currency.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Row 5: Department, Location, Auto Renewal */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Department
            </label>
            <select
              name="department"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.department}
              onChange={handleChange}
            >
              <option value="">Select Department</option>
              {masterData.departments.map(department => (
                <option key={department.value} value={department.value}>
                  {department.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Location
            </label>
            <select
              name="location"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.location}
              onChange={handleChange}
            >
              <option value="">Select Location</option>
              {masterData.locations.map(location => (
                <option key={location.value} value={location.value}>
                  {location.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Auto Renewal
            </label>
            <div className="form-check-container">
              <input
                className="form-check-input"
                type="checkbox"
                name="autoRenewal"
                checked={formData.autoRenewal}
                onChange={handleChange}
              />
              <label className="form-check-label">
                Enable automatic renewal
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Row 5.5: Approval & Review Requirements */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Approval Level
            </label>
            <select
              name="approvalLevel"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.approvalLevel}
              onChange={handleChange}
            >
              <option value="Department">Department Level</option>
              <option value="Manager">Manager Level</option>
              <option value="Director">Director Level</option>
              <option value="VP">VP Level</option>
              <option value="C-Level">C-Level</option>
              <option value="Board">Board Level</option>
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Legal Review Required
            </label>
            <div className="form-check-container">
              <input
                className="form-check-input"
                type="checkbox"
                name="requiresLegalReview"
                checked={formData.requiresLegalReview}
                onChange={handleChange}
              />
              <label className="form-check-label">
                Legal review required
              </label>
            </div>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Finance Review Required
            </label>
            <div className="form-check-container">
              <input
                className="form-check-input"
                type="checkbox"
                name="requiresFinanceReview"
                checked={formData.requiresFinanceReview}
                onChange={handleChange}
              />
              <label className="form-check-label">
                Finance review required
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Row 5.6: Legal & Compliance */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Governing Law
            </label>
            <select
              name="governingLaw"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.governingLaw}
              onChange={handleChange}
            >
              <option value="">Select Governing Law</option>
              <option value="US Federal">US Federal Law</option>
              <option value="State of California">State of California</option>
              <option value="State of New York">State of New York</option>
              <option value="State of Texas">State of Texas</option>
              <option value="English Law">English Law</option>
              <option value="German Law">German Law</option>
              <option value="French Law">French Law</option>
              <option value="International">International Law</option>
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Jurisdiction
            </label>
            <input
              type="text"
              name="jurisdiction"
              className="form-control"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.jurisdiction}
              onChange={handleChange}
              placeholder="e.g., New York, NY"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Notice Period
            </label>
            <select
              name="noticePeriod"
              className="form-select"
              style={{
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                padding: '10px 12px',
                fontSize: '13px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={formData.noticePeriod}
              onChange={handleChange}
            >
              <option value="">Select Notice Period</option>
              <option value="30 days">30 days</option>
              <option value="60 days">60 days</option>
              <option value="90 days">90 days</option>
              <option value="6 months">6 months</option>
              <option value="1 year">1 year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 6: Description (Full Width) */}
      <div className="form-row" style={{ width: '100%' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Description
          </label>
          <textarea
            name="description"
            className="form-control"
            style={{
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              padding: '10px 12px',
              fontSize: '13px',
              width: '100%',
              boxSizing: 'border-box',
              minHeight: '80px',
              resize: 'vertical'
            }}
            value={formData.description}
            onChange={handleChange}
            rows="3"
            placeholder="Enter contract description and additional details"
          />
        </div>
      </div>

      {/* File Upload Section */}
      <div className="form-row" style={{ width: '100%' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Contract Documents
          </label>

          {/* File Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragOver ? '#007B8F' : '#d1d5db'}`,
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver ? '#f0f9ff' : '#fafafa',
              transition: 'all 0.2s ease-in-out',
              marginBottom: '12px'
            }}
          >
            <Upload size={32} style={{ color: '#007B8F', marginBottom: '8px' }} />
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
              {isDragOver ? 'Drop files here' : 'Click to upload or drag and drop'}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB each)
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Uploaded Files ({uploadedFiles.length})
              </div>
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#f9fafb',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: '13px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <div style={{ color: '#007B8F' }}>
                        {getFileIcon(file.type)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '500', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="btton"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '24px', width: '100%' }}>
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: 'white'
        }}>

          {/* Render ClauseSelector when toggled */}
          {showClauseSelector && (
            <div className="mb-4 p-3 bg-light rounded border">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="m-0 text-muted" style={{ fontSize: '13px' }}>Select Clauses</h6>
                <button type="button" className="btn btn-sm text-danger p-0" onClick={() => setShowClauseSelector(false)}>
                  <X size={16} />
                </button>
              </div>
              <ClauseSelector
                onSelect={(clause) => {
                  const newClauses = [...(formData.clauses || []), clause];
                  handleClausesChange(newClauses);
                  setShowClauseSelector(false);
                }}
                onClose={() => setShowClauseSelector(false)}
              />
            </div>
          )}

          <ClauseManager
            clauses={formData.clauses}
            onClausesChange={handleClausesChange}
            contractType={formData.type}
          />
        </div>
      </div>

      {/* Approval Workflow Section */}
      <div style={{ marginBottom: '24px', width: '100%' }}>
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: 'white'
        }}>
          <ApprovalWorkflow
            approvalStatus={formData.approvalStatus}
            approvalLevel={formData.approvalLevel}
            approvers={formData.approvers}
            approvalHistory={formData.approvalHistory}
            onApprovalChange={handleApprovalChange}
          />
        </div>
      </div>
      {/* WARNING CONFIRMATION MODAL */}
      {showWarningModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', width: '500px', maxWidth: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              backgroundColor: '#FEF3C7', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '12px',
              borderBottom: '1px solid #FCD34D'
            }}>
              <div style={{
                backgroundColor: '#F59E0B', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AlertTriangle size={24} color="white" />
              </div>
              <h3 style={{ margin: 0, color: '#92400E', fontSize: '18px', fontWeight: '600' }}>Policy Warning</h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <p style={{ marginTop: 0, color: '#4B5563', fontSize: '14px', marginBottom: '16px' }}>
                This contract violates one or more non-critical compliance policies. Do you want to proceed anyway?
              </p>

              <div style={{ backgroundColor: '#FFFBEB', borderRadius: '6px', padding: '12px', border: '1px solid #FDE68A' }}>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#B45309' }}>
                  {warningMessages.map((msg, idx) => (
                    <li key={idx} style={{ marginBottom: '6px', fontSize: '14px' }}>
                      <strong>{msg.policyName}:</strong> {msg.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px', backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'flex-end', gap: '12px'
            }}>
              <button
                type="button"
                onClick={() => setShowWarningModal(false)}
                className="btn"
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #D1D5DB', backgroundColor: 'white',
                  color: '#374151', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmWarnings}
                className="btn"
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#F59E0B',
                  color: 'white', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Proceed with Warnings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          style={{
            padding: '10px 20px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            backgroundColor: '#f9fafb',
            color: '#374151',
            fontSize: '13px',
            fontWeight: '500',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          style={{
            padding: '10px 20px',
            borderRadius: '6px',
            backgroundColor: '#007B8F',
            border: '1px solid #007B8F',
            color: 'white',
            fontSize: '13px',
            fontWeight: '500',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}
        >
          {contract ? 'Update Contract' : 'Create Contract'}
        </button>
      </div>
    </form>
  );
};

export default ContractForm;